import { createFileRoute, Link } from "@tanstack/react-router";
import axios from "axios";
import { ptBR } from "date-fns/locale";
import { ArrowLeft, BookMarked, Info } from "lucide-react";
import { useEffect, useState } from "react";
import { ApplicationRank } from "@/components/exam-result/application-rank";
import { DisciplinesBreakdown } from "@/components/exam-result/disciplines-breakdown";
import { QuestionReviewSheet } from "@/components/exam-result/question-review-sheet";
import {
	QuestionsOverview,
	type ResultQuestion,
} from "@/components/exam-result/questions-overview";
import { QuestionsToReview } from "@/components/exam-result/questions-to-review";
import { ResultIndicators } from "@/components/exam-result/result-indicators";
import { SentFilesTable } from "@/components/exam-result/sent-files-table";
import { TRIPerformance } from "@/components/exam-result/tri-performance";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApplicationResult } from "@/hooks/queries/use-application-result";
import { useModal } from "@/hooks/use-modal";
import { parseLocalDateOnly, safeFormat } from "@/lib/utils";

type ResultTab = "general" | "review";

const RESULT_TABS: { id: ResultTab; label: string }[] = [
	{ id: "general", label: "Informações Gerais" },
	{ id: "review", label: "Questões para revisar" },
];

export const Route = createFileRoute("/_app/painel/minhas-provas/$id")({
	component: ExamResultPage,
});

function ExamResultPage() {
	const { id } = Route.useParams();
	const { data, isLoading, error } = useApplicationResult(id);
	const { openModal } = useModal();

	// Escopo de navegação do painel: a lista visível no momento em que a questão
	// foi aberta, para que ‹ › respeitem o filtro e a ordenação escolhidos.
	const [navScope, setNavScope] = useState<ResultQuestion[]>([]);
	const [activeQuestionId, setActiveQuestionId] = useState<
		string | number | null
	>(null);
	const [tab, setTab] = useState<ResultTab>("general");

	const activeIndex = navScope.findIndex((q) => q.id === activeQuestionId);
	const activeQuestion = activeIndex >= 0 ? navScope[activeIndex] : null;
	const previousQuestion = activeIndex > 0 ? navScope[activeIndex - 1] : null;
	const nextQuestion =
		activeIndex >= 0 && activeIndex < navScope.length - 1
			? navScope[activeIndex + 1]
			: null;

	const openSubjectPerformance = (subject: {
		subjectId: string;
		subject: string;
	}) => {
		openModal("SubjectPerformanceModal", {
			applicationId: id,
			subjectId: subject.subjectId,
			subjectName: subject.subject,
		});
	};

	useEffect(() => {
		if (data?.application?.exam?.name) {
			document.title = data.application.exam.name;
		}
		return () => {
			document.title = "Lize";
		};
	}, [data]);

	if (isLoading) {
		return (
			<div className="flex flex-col gap-6 p-6 lg:max-w-6xl lg:mx-auto">
				<Skeleton className="h-4 w-32" />
				<div className="flex justify-between items-center">
					<Skeleton className="h-10 w-48" />
					<Skeleton className="h-20 w-80" />
				</div>
				<div className="grid grid-cols-3 gap-10">
					<Skeleton className="h-32 w-full" />
					<Skeleton className="h-32 w-full" />
					<Skeleton className="h-32 w-full" />
				</div>
			</div>
		);
	}

	if (error) {
		const status = axios.isAxiosError(error)
			? error.response?.status
			: undefined;

		const message =
			status === 401
				? "Você não tem permissão para visualizar este resultado."
				: "Ocorreu um erro ao carregar os resultados.";

		return (
			<div className="flex flex-col items-center justify-center h-[50vh] gap-4">
				<p className="text-slate-500 font-medium text-lg">{message}</p>
				<Button asChild variant="outline">
					<Link
						to="/painel/minhas-provas"
						search={{
							status: "disponiveis",
							page: 1,
							year: new Date().getFullYear().toString(),
						}}
					>
						Voltar para a lista
					</Link>
				</Button>
			</div>
		);
	}

	if (!data) {
		return (
			<div className="flex flex-col items-center justify-center h-[50vh] gap-4">
				<p className="text-slate-500 font-medium text-lg">
					Resultado não encontrado.
				</p>
				<Button asChild variant="outline">
					<Link
						to="/painel/minhas-provas"
						search={{
							status: "disponiveis",
							page: 1,
							year: new Date().getFullYear().toString(),
						}}
					>
						Voltar para a lista
					</Link>
				</Button>
			</div>
		);
	}

	const filesConcat = data.files
		? Object.entries(data.files).flatMap(([key, items]) =>
				items.map((item) => ({ ...item, category: key })),
			)
		: [];

	const exam = data.application?.exam;

	return (
		<div className="flex flex-col gap-2 py-6 px-4 lg:py-10 lg:max-w-6xl lg:mx-auto">
			<Link
				to="/painel/minhas-provas"
				search={{
					status: "disponiveis",
					page: 1,
					year: new Date().getFullYear().toString(),
				}}
				className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors w-fit"
			>
				<ArrowLeft size={16} />
				Minhas provas
			</Link>

			<div className="flex flex-col lg:flex-row justify-between items-start lg:items-center pt-6 gap-6">
				<div className="flex flex-col gap-1">
					<h1 className="text-3xl font-bold text-slate-900">Resultados</h1>
					<p className="text-slate-500 font-medium">
						Veja como foi seu desempenho
					</p>
				</div>
				<Card className="flex flex-row items-center gap-4 px-6 py-4 bg-white shadow-sm rounded-xl border-slate-100">
					<div className="size-12 shrink-0 rounded-full bg-slate-50 flex items-center justify-center">
						<BookMarked className="text-primary" size={24} />
					</div>
					<div className="flex min-w-0 flex-col gap-0.5">
						<h4 className="font-bold text-slate-900">
							{/* O payload do resultado não traz a categoria da aplicação, só
							    `{ id, date, exam? }` — sem como distinguir avaliação de lista
							    de exercício aqui. Fallback neutro em vez de chamar de
							    "Avaliação" algo que pode ser uma lista. */}
							{exam?.name ?? "Sem título"}
						</h4>
						<span className="text-sm font-semibold text-slate-400">
							{safeFormat(parseLocalDateOnly(data.application?.date), "PPP", {
								locale: ptBR,
							})}
						</span>
					</div>
				</Card>
			</div>

			<div className="h-px bg-slate-100 w-full mt-8" />

			{/* Pílula compacta no mobile e abas em caixa no desktop, como na tela de
			    resultado do design system. */}
			<div className="pt-8">
				<Tabs
					value={tab}
					onValueChange={(value) => setTab(value as ResultTab)}
					className="w-full gap-0 sm:hidden"
				>
					<TabsList size="sm" className="w-full">
						{RESULT_TABS.map((option) => (
							<TabsTrigger
								key={option.id}
								size="sm"
								value={option.id}
								className="flex-1"
							>
								{option.label}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>

				<Tabs
					value={tab}
					onValueChange={(value) => setTab(value as ResultTab)}
					className="hidden w-full gap-0 sm:flex"
				>
					<TabsList size="lg">
						{RESULT_TABS.map((option) => (
							<TabsTrigger key={option.id} size="lg" value={option.id}>
								{option.label}
							</TabsTrigger>
						))}
					</TabsList>
				</Tabs>
			</div>

			{tab === "review" ? (
				<div className="pt-8">
					<QuestionsToReview
						applicationId={id}
						subjects={data.subjects}
						questions={data.questionsData}
						onSelectQuestion={(question, scope) => {
							setNavScope(scope);
							setActiveQuestionId(question.id);
						}}
					/>
				</div>
			) : (
				<>
					{exam?.isEnemSimulator ? (
						<TRIPerformance
							examId={exam.id}
							questionsCount={data.questionsCount}
						/>
					) : (
						<ResultIndicators
							questionsCount={data.questionsCount}
							performance={data.performance}
							score={data.score}
						/>
					)}

					{data.applicationRank && (
						<ApplicationRank rank={data.applicationRank} />
					)}

					<div className="space-y-8 pt-8">
						<QuestionsOverview
							questions={data.questionsData}
							onSelectQuestion={(question, visibleQuestions) => {
								setNavScope(visibleQuestions);
								setActiveQuestionId(question.id);
							}}
						/>

						<div className="flex flex-col gap-10 pt-10">
							<DisciplinesBreakdown
								subjects={data.subjects}
								questions={data.questionsData}
								onSelectSubject={openSubjectPerformance}
								onViewArea={(areaQuestions) => {
									if (areaQuestions.length === 0) return;
									setNavScope(areaQuestions);
									setActiveQuestionId(areaQuestions[0].id);
								}}
							/>

							{/* Arquivos */}
							<div className="space-y-6">
								<h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
									Arquivos enviados{" "}
									<Info size={18} className="text-slate-400" />
								</h2>
								<SentFilesTable files={filesConcat} />
							</div>
						</div>
					</div>
				</>
			)}

			<QuestionReviewSheet
				applicationId={id}
				open={activeQuestion !== null}
				onOpenChange={(open) => {
					if (!open) setActiveQuestionId(null);
				}}
				question={activeQuestion}
				previousQuestion={previousQuestion}
				nextQuestion={nextQuestion}
				onNavigate={(question) => setActiveQuestionId(question.id)}
			/>
		</div>
	);
}
